/**
 * Iter 67w — Per-vehicle pickup payment methods chip strip.
 *
 * Single source of truth for rendering the host's accepted-at-pickup methods
 * across the public car detail page, the booking widget, and the booking
 * summary. The backend resolves `pickup_payment_methods_resolved` on every car
 * GET so this component never needs to do its own lookup — just `methods`
 * (the resolved `[{key, label}]` list) and an optional compact density.
 *
 * Used on:
 *   • CarDetail.jsx (public car page)
 *   • BookingWidget.jsx (during checkout — labelled "What you'll pay at pickup")
 *   • Customer Dashboard booking cards (post-booking summary)
 */
import {
  Banknote, CreditCard, Wallet, Link as LinkIcon, Landmark, Smartphone,
} from "lucide-react";

const ICONS = {
  cash:           Banknote,
  card_on_pickup: CreditCard,
  paypal:         Wallet,
  stripe:         LinkIcon,
  bank_transfer:  Landmark,
  revolut:        Smartphone,
};

const TONE = {
  cash:           { bg: "bg-emerald-50",  text: "text-emerald-700",  ring: "ring-emerald-200" },
  card_on_pickup: { bg: "bg-blue-50",     text: "text-blue-700",     ring: "ring-blue-200" },
  paypal:         { bg: "bg-sky-50",      text: "text-sky-700",      ring: "ring-sky-200" },
  stripe:         { bg: "bg-violet-50",   text: "text-violet-700",   ring: "ring-violet-200" },
  bank_transfer:  { bg: "bg-teal-50",     text: "text-teal-700",     ring: "ring-teal-200" },
  revolut:        { bg: "bg-neutral-100", text: "text-neutral-800",  ring: "ring-neutral-300" },
};

export default function PickupPaymentMethods({
  methods = [],
  compact = false,
  className = "",
  testId = "pickup-payment-methods",
}) {
  if (!Array.isArray(methods) || methods.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`} data-testid={testId}>
      {methods.map((m) => {
        const Icon = ICONS[m.key] || CreditCard;
        const tone = TONE[m.key] || TONE.card_on_pickup;
        return (
          <span
            key={m.key}
            className={`inline-flex items-center gap-1 ${tone.bg} ${tone.text} ring-1 ${tone.ring} rounded-full font-semibold ${
              compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"
            }`}
            data-testid={`${testId}-${m.key}`}
            title={m.label}
          >
            <Icon className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
            {m.label}
          </span>
        );
      })}
    </div>
  );
}
